import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { 
  Menu, X, Home, Users, Trash2, FileText, Shield, Lock, Mail, 
  UserPlus, BarChart3, Brain, Send, CreditCard, Building2, Zap, Calculator, Printer, IdCard, PenLine, MessageSquare, LayoutList, Activity
} from "lucide-react";
import { Button } from "./ui/button";

const navItems = [
  { path: "/", label: "Dashboard", icon: Home },
  { path: "/activity", label: "Last 24 Hours", icon: Activity },
  { path: "/clients", label: "Clients", icon: Users },
  { path: "/client-updates", label: "Client Updates", icon: MessageSquare },
  { path: "/trash", label: "Trash", icon: Trash2 },
];

const toolItems = [
  { path: "/dispute-wizard/new", label: "Dispute Wizard", icon: Shield, color: "text-red-400" },
  { path: "/pre-screening", label: "Pre-Screening", icon: Calculator, color: "text-emerald-400" },
  { path: "/client-progress", label: "Client Progress", icon: BarChart3, color: "text-blue-400" },
  { path: "/payday-st", label: "Payday ST", icon: FileText, color: "text-amber-400" },
  { path: "/copy-fax", label: "Copy & Fax Converter", icon: Printer, color: "text-blue-400" },
  { path: "/id-converter", label: "ID + SSN Converter", icon: IdCard, color: "text-cyan-400" },
  { path: "/handwriting-converter", label: "Handwritten Converter", icon: PenLine, color: "text-violet-400" },
  { path: "/letter-intelligence", label: "Letter Intelligence", icon: Brain, color: "text-violet-300" },
  { path: "/pdf-finalize", label: "PDF Finalize", icon: Lock, color: "text-purple-400" },
  { path: "/account-summary-breakdown", label: "Account Summary Breakdown", icon: LayoutList, color: "text-fuchsia-400" },
  { path: "/just-mail", label: "Mail", icon: Mail, color: "text-purple-400" },
  { path: "/send-intake", label: "Send Intake", icon: UserPlus, color: "text-teal-400" },
  { path: "/analyze-report", label: "Analyze Report", icon: BarChart3, color: "text-blue-400" },
  { path: "/credit-repair-steps", label: "Credit Repair Steps", icon: Send, color: "text-green-400" },
  { path: "/tradeline-manager", label: "Tradeline Manager", icon: CreditCard, color: "text-pink-400" },
  { path: "/tradeline-world", label: "Tradeline World", icon: CreditCard, color: "text-fuchsia-400" },
  { path: "/credit-builder-list", label: "Credit Builder List", icon: Building2, color: "text-cyan-400" },
];

export default function MobileNav() {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <>
      {/* Hamburger Menu Button - Fixed position */}
      <div className="fixed top-4 left-4 z-50">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setMenuOpen(!menuOpen)}
          className="bg-zinc-900 hover:bg-zinc-800 border border-zinc-700"
          data-testid="mobile-nav-toggle"
        >
          {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
      </div>

      {/* Slide-out Navigation Panel */}
      <aside 
        className={`fixed inset-y-0 left-0 z-40 w-72 bg-zinc-900 border-r border-zinc-800 transform transition-transform duration-300 ease-in-out ${
          menuOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-zinc-800">
            <Link to="/" onClick={() => setMenuOpen(false)} className="flex items-center gap-2">
              <img src="/images/clear-all-wizard-logo.png" alt="Clear All Wizard" className="h-12 w-auto" />
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {/* Main Nav */}
            <p className="px-4 text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2">Navigation</p>
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                    isActive
                      ? "bg-orange-500/20 text-orange-400 border border-orange-500/30"
                      : "text-zinc-400 hover:text-white hover:bg-zinc-800"
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}
            
            {/* Tools Section */}
            <div className="pt-4 mt-4 border-t border-zinc-700">
              <p className="px-4 text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2">Tools</p>
              {toolItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path || location.pathname.startsWith(item.path.replace('/new', ''));
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all ${
                      isActive
                        ? "bg-zinc-800 text-white border border-zinc-600"
                        : "text-zinc-400 hover:text-white hover:bg-zinc-800"
                    }`}
                  >
                    <Icon className={`h-4 w-4 ${item.color}`} />
                    <span className="text-sm">{item.label}</span>
                  </Link>
                );
              })}
            </div>
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-zinc-800">
            <p className="text-xs text-zinc-500 text-center">v2.0 - Clear All Wizard</p>
          </div>
        </div>
      </aside>

      {/* Overlay when menu is open */}
      {menuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30"
          onClick={() => setMenuOpen(false)}
        />
      )}
    </>
  );
}
